/**
 * Passenger identity on the v2 contract.
 *
 * One axios instance for login, registration, OTP, password reset and the
 * account profile — all now under the same /api/v2 base as the booking
 * journey. Before this the site held TWO bases and TWO response shapes for a
 * single user's session: the booking flow spoke v2 while identity spoke the
 * legacy { success, ...flat } shape on a different host.
 *
 * THE INTERCEPTOR IS THE POINT.
 * -----------------------------
 * v2 answers { ok, code, message, data }. Call sites were written against the
 * legacy flat body and read `response.data.access_token` directly. Unwrapping
 * `data` here means the contract changes without touching the reading code in
 * three components — and if a call site is ever moved back, it still works.
 *
 * `message` and `ok` are kept alongside the unwrapped payload because several
 * screens surface the server's own wording rather than inventing their own.
 *
 * Failures are left ALONE: the v2 error body is { ok, code, message, errors },
 * and every call site already reads `error.response?.data?.message`, which is
 * exactly where it lands.
 */
import axios from "axios";
import { TICKETS_API_URL } from "@/config/config";

const identity = axios.create({
  baseURL: TICKETS_API_URL,
  // Matches apiV2: a distant DB has answered slower than 20s under load.
  timeout: 60000,
  headers: { "Content-Type": "application/json", Accept: "application/json" },
});

function unwrapEnvelope(response) {
  const body = response.data;

  if (!body || typeof body !== "object" || !Object.prototype.hasOwnProperty.call(body, "ok")) {
    return response;
  }

  /*
   * LIST PAYLOADS ARE LEFT ALONE.
   *
   * The v2 contract makes `data` a BARE ARRAY for list endpoints. Spreading an
   * array into an object turns [a, b] into { 0: a, 1: b } — it would not throw,
   * it would just quietly stop being a list, and the page would render nothing
   * with no error to follow. So arrays keep the envelope and their call sites
   * read `response.data.data` directly.
   */
  if (Array.isArray(body.data)) {
    return response;
  }

  response.data = {
    ...(body.data && typeof body.data === "object" ? body.data : {}),
    ok: body.ok,
    message: body.message,
  };

  return response;
}

identity.interceptors.response.use(unwrapEnvelope);

/**
 * Apply the same unwrapping to an instance that needs its own headers.
 *
 * UserProfilePage builds its own instance so it can carry the passenger's
 * bearer token; it still has to read the same envelope, and duplicating the
 * logic there is how the two drift apart.
 */
export function useEnvelope(instance) {
  instance.interceptors.response.use(unwrapEnvelope);
  return instance;
}

/** Attach the passenger's bearer token to one request. */
export function withAuth(token) {
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

export default identity;
